import type { JsonObject } from '../../app/json/jsonValue.js';

/**
 * Purpose:
 * Endpoint-facing security policy seam, resolved from RegistryStore the same way
 * data/view/action handlers are. Lets the web endpoint apply authentication and role gating
 * without naming any concrete security type, so a host that does not register a security
 * implementation still protects machine endpoints through the DenySecurityGate default.
 *
 * Responsibilities:
 * - Report whether security gating is active for the running host
 * - Classify AppSites, AppViews, and actions as secured
 * - Answer authentication and role-authorization questions for a request context
 *
 * Ordering / security assumptions:
 * - The concrete gate is registered at startup before request handling begins
 * - When no gate is registered, human gating remains inactive but OCI requests require authentication
 */
export interface ISecurityGate {
    /** Whether security gating is active. When false the endpoint skips all gating. */
    get enabled(): boolean;

    /** Whether the supplied AppSite is the security-owning AppSite. */
    isSecurityAppSite(appSite: string): boolean;

    /** Whether the supplied AppView is gated (requires authentication/authorization). */
    isSecuredAppView(appView: string): boolean;

    /** Whether the supplied action name is gated (requires authentication/authorization). */
    isSecuredAction(action: string): boolean;

    /** Whether the supplied role is allowed to access the gated AppView. */
    areRolesAllowedForView(appView: string, roles: readonly string[]): boolean;

    /** Whether the supplied role is allowed to invoke the gated action. */
    areRolesAllowedForAction(action: string, roles: readonly string[]): boolean;

    /** Whether the request context represents an authenticated caller. */
    isAuthenticated(contextJson: JsonObject): boolean;

    /**
     * Whether the OCI server requires API-key authentication (Phase 8 A3, opt-in). When false the OCI
     * endpoint runs with legacy anonymous access and never consults authenticateOciBearer.
     */
    get ociAuthEnabled(): boolean;

    /** The realm advertised in the OCI 401 `WWW-Authenticate: Bearer realm="..."` challenge. */
    get ociAuthRealm(): string;

    /**
     * Authenticates an OCI request from its raw Authorization header value ("Bearer <KeyId>.<secret>").
     * Returns the owning user id on a valid + enabled + non-expired key, or null when the header is
     * missing/malformed/invalid. The endpoint reads this only when ociAuthEnabled is true.
     */
    authenticateOciBearer(authorizationHeaderValue: string | null): string | null;
}

/**
 * Safe default used when no security implementation is registered. Human AppSite classification stays
 * inactive because it has no configured policy, while OCI machine endpoints require a credential that
 * cannot be resolved without a registered concrete gate.
 */
export class DenySecurityGate implements ISecurityGate {
    get enabled(): boolean { return false; }
    isSecurityAppSite(appSite: string): boolean { return false; }
    isSecuredAppView(appView: string): boolean { return false; }
    isSecuredAction(action: string): boolean { return false; }
    areRolesAllowedForView(appView: string, roles: readonly string[]): boolean { return false; }
    areRolesAllowedForAction(action: string, roles: readonly string[]): boolean { return false; }
    isAuthenticated(contextJson: JsonObject): boolean { return false; }
    get ociAuthEnabled(): boolean { return true; }
    get ociAuthRealm(): string { return 'Arshu OCI'; }
    authenticateOciBearer(authorizationHeaderValue: string | null): string | null { return null; }
}
